import type { ReactNode } from 'react';
import { SecondaryButtonClassname } from '../buttons';
import { Popover } from '../popover/popover';
import { PopoverHeader } from './popover-header';

type ToolbarPopoverProps = {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  minWidth?: string;
  /**
   * Count badge on the trigger button. Any number — including 0 — renders a
   * pill: muted when 0, dark when positive. The pill keeps the same dimensions
   * in both states so the button never resizes when the first filter is added.
   * Omit it entirely (undefined) to render no pill.
   */
  badge?: number;
  /** When provided, the popover gets a header with a "Clear all" action. */
  onClear?: () => void;
};

export function ToolbarPopover({
  icon,
  label,
  children,
  minWidth = 'min-w-96',
  badge,
  onClear,
}: ToolbarPopoverProps) {
  return (
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={`${SecondaryButtonClassname} flex items-center justify-center`}
        >
          <span className="text-icon">{icon}</span>
          <span>{label}</span>
          {badge !== undefined && (
            <span
              className={`ml-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold leading-none tabular-nums ${
                badge > 0 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'
              }`}
            >
              {badge}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Content className={`${minWidth} space-y-4`} align="start">
        {onClear && (
          <PopoverHeader
            label={label}
            action={{ label: 'Clear all', onClick: onClear, disabled: badge === 0 }}
          />
        )}
        {children}
      </Popover.Content>
    </Popover>
  );
}
